import type { CompanionStatus } from '../companion/ScoutCompanion';
import { distance, eulerFromQuat, RAD2DEG } from '../sim/mathx';
import { isPointInFrustum, SCOUT_CAMERA_CONFIG, scoutCameraPose } from '../sim/vision';
import type { InterpolatedTelemetry } from '../state/interpolate';
import type { TelemetrySnapshot } from '../state/telemetryBuffer';
import {
  formatAltitude,
  formatDuration,
  formatHeadingDeg,
  formatPercent,
  formatSpeed,
  formatVoltage,
  type DisplayUnits,
} from './format';
import type { LapState } from './lapTimer';

export const BATTERY_SCALE_WARNING_THRESHOLD = 0.85;
/** The vendored scout airframe's configured maximum steady-state motor speed. */
export const DEFAULT_MAX_MOTOR_RPM = 9500;

export interface HudModelInputs {
  interp: InterpolatedTelemetry;
  telemetry: TelemetrySnapshot;
  companion: CompanionStatus;
  units: DisplayUnits;
  /** Explicit 0..1 vehicle power scale, supplied because it is absent from the snapshot. */
  throttleScale: number;
  /** Maximum RPM used to estimate throttle from mean snapshot motor RPM. */
  maxMotorRpm?: number;
  classForTrack?: (id: number) => string;
  lap?: LapState;
  /** Running minimum maintained by the caller/metrics accumulator. */
  minSeparationM?: number | null;
  /** World-ENU ground height. Defaults to the ENU zero plane. */
  groundElevationM?: number;
  /** MSL altitude of the ENU origin. Defaults to zero when unavailable. */
  originAltitudeMslM?: number;
}

export interface HudViewModel {
  flight: {
    attitude: { rollRad: number; pitchRad: number };
    altAglText: string;
    altMslText: string;
    groundSpeedText: string;
    verticalSpeedText: string;
    headingText: string;
    throttlePctText: string;
    armedText: 'ARMED' | 'DISARMED';
    modeText: string;
    batteryText: string;
    batteryBarFraction: number;
    batteryWarning: boolean;
    crashFlag: boolean;
    lapText: string;
  };
  mission: {
    companionStateText: string;
    selectedTrackText: string;
    slantRangeText: string;
    timeToStationText: string;
    confirmedTrackCount: number;
    targetInFrustum: boolean;
    minSeparationText: string;
  };
}

const clamp01 = (value: number): number =>
  Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0;

export const buildHudModel = (inputs: HudModelInputs): HudViewModel => {
  const { interp, telemetry, companion, units } = inputs;
  const euler = eulerFromQuat(interp.scout.attitude);
  const groundElevation = inputs.groundElevationM ?? 0;
  const originAltitudeMsl = inputs.originAltitudeMslM ?? 0;
  const selectedTrack = interp.tracks.find((track) => track.id === companion.selectedTrackId);
  const selectedPosition = selectedTrack?.posEnu ?? companion.targetPosition;
  const computedRange =
    selectedPosition === null || selectedPosition === undefined
      ? companion.rangeToTarget
      : distance(interp.scout.posEnu, selectedPosition);
  const currentOrAccumulatedSeparation = inputs.minSeparationM ?? computedRange;
  const cameraPose = scoutCameraPose(interp.scout, SCOUT_CAMERA_CONFIG.tiltDeg);
  const targetInFrustum =
    selectedPosition !== null && selectedPosition !== undefined
      ? isPointInFrustum(
          cameraPose,
          SCOUT_CAMERA_CONFIG.fovDeg,
          SCOUT_CAMERA_CONFIG.aspect,
          SCOUT_CAMERA_CONFIG.near,
          SCOUT_CAMERA_CONFIG.far,
          selectedPosition,
        )
      : false;
  const selectedTrackText =
    companion.selectedTrackId === null
      ? '-'
      : `#${companion.selectedTrackId} ${inputs.classForTrack?.(companion.selectedTrackId) ?? 'unknown'}`;
  const lapSeconds =
    inputs.lap?.currentLapStart !== null && inputs.lap?.currentLapStart !== undefined
      ? Math.max(0, interp.time - inputs.lap.currentLapStart)
      : inputs.lap?.lastLapTime;
  const throttleScale = clamp01(inputs.throttleScale);
  const maxMotorRpm = inputs.maxMotorRpm ?? DEFAULT_MAX_MOTOR_RPM;
  const meanMotorRpm =
    telemetry.scout.motorRpm.reduce((sum, motorRpm) => sum + motorRpm, 0) /
    telemetry.scout.motorRpm.length;
  const throttleFraction = maxMotorRpm > 0 ? clamp01(meanMotorRpm / maxMotorRpm) : 0;

  return {
    flight: {
      attitude: { rollRad: euler.roll, pitchRad: euler.pitch },
      altAglText: formatAltitude(interp.scout.posEnu.z - groundElevation, units.length),
      altMslText: formatAltitude(interp.scout.posEnu.z + originAltitudeMsl, units.length),
      groundSpeedText: formatSpeed(
        Math.hypot(telemetry.scout.velEnu.x, telemetry.scout.velEnu.y),
        units.speed,
      ),
      verticalSpeedText: formatSpeed(telemetry.scout.velEnu.z, units.speed),
      // ENU yaw is CCW from east; compass heading is clockwise from north.
      headingText: formatHeadingDeg(90 - euler.yaw * RAD2DEG),
      throttlePctText: formatPercent(throttleFraction),
      armedText: telemetry.scout.flightController.armed ? 'ARMED' : 'DISARMED',
      modeText: telemetry.scout.flightController.mode.toUpperCase(),
      batteryText: formatVoltage(telemetry.scout.batteryV),
      batteryBarFraction: throttleScale,
      batteryWarning: inputs.throttleScale < BATTERY_SCALE_WARNING_THRESHOLD,
      crashFlag: telemetry.scout.flightController.crashed,
      lapText: formatDuration(lapSeconds),
    },
    mission: {
      companionStateText: companion.state,
      selectedTrackText,
      slantRangeText: formatAltitude(computedRange ?? Number.NaN, units.length),
      timeToStationText: formatDuration(companion.timeToStation),
      confirmedTrackCount: interp.tracks.filter((track) => track.state === 'confirmed').length,
      targetInFrustum,
      minSeparationText: formatAltitude(currentOrAccumulatedSeparation ?? Number.NaN, units.length),
    },
  };
};
